import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Button,
  Tabs,
  Tab,
  Alert,
  Spinner,
  Row,
  Col,
} from "react-bootstrap";
import { contentService } from "../../services/contentService";

const ContentManagement = () => {
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  // DEFAULTS: Pre-fill with your current hardcoded text
  const [homeContent, setHomeContent] = useState({
    h1: "Boutique Luxury Vacation Homes in Mountain & Coastal Destinations",
    introText:
      "ACF Luxury Stays offers a deliberately hand-selected collection of premier residences – from slopeside chalets in Telluride and Beaver Creek to a luxury poolside retreat in Del Mar, San Diego.",
    secondary_intro_p1:
      "Discover why we’ve chosen to call these homes our own as you enjoy the rugged peaks of the San Juan Mountains or sun-drenched tranquility of the Southern California coast.",
    secondary_intro_p2:
      "As family owners who personally oversee every detail, we deliver a level of intentionality, care, and hotel-standard accountability that only true ownership can provide. Experience the warmth of a private home, meticulously curated for the most discerning travelers planning their next vacation.",
    h2_destinations: "Explore Our Luxury Vacation Homes",
    h2_why_choose: "Why Choose ACF Luxury Stays?",
    why_choose_subtext:
      "We offer a deliberately small collection of luxury homes, each personally overseen and professionally managed to hotel-level standards.",
    testimonial_1:
      "Chris was the most incredible host - a top notch experience from the very beginning. Chris and team were extremely responsive, friendly, and helpful. The property is pristine and in a great relocation with Westin amenities. Can’t recommend this property enough!! Thank you for an amazing stay.",
    testimonial_2:
      "This place was above our expectations. The cozy feel of his place was exactly what we were looking for. Everything in mountain village is in walking distance as advertised. Our family is already making plans for a return stay. Chris would respond immediately to any questions we had. I would highly recommend this rental for a stay anytime of the year.",
  });

  const [aboutContent, setAboutContent] = useState({
    h1: "About ACF Luxury Stays",
    introCopy:
      "At ACF Luxury Stays, we believe that true luxury isn’t just found in high-end finishes—it’s found in the peace of mind that comes from meticulous, personal oversight. Our boutique brand was born from a deep appreciation for the rugged alpine majesty of the Colorado Rockies and the sun-drenched, sophisticated shores of Southern California. We offer a deliberately small collection designed for travelers who seek a sanctuary curated with intentionality and care.",
    h2_family: "Family Owned & Intentionally Operated",
    familyCopy:
      "We aren’t a large-scale management agency; we are family owners who have chosen to call these properties our own. This intentional approach allows us to bridge the gap between the professional accountability of a luxury hotel and the authentic warmth of a private home.",
    standard_h2: "The ACF Standard",
    card1_title: "Personal Oversight",
    card1_text:
      "Every home in our collection is personally overseen by our family team to ensure it meets our exacting standards. We don't rely on remote third parties; we are hands-on.",
    card2_title: "Hotel-Level Excellence",
    card2_text:
      "We maintain a rigorous commitment to professional-grade cleaning and maintenance, ensuring your home is pristine, stocked, and ready upon your arrival.",
    card3_title: "Curated Details",
    card3_text:
      "From the high-end chef’s kitchens to the artwork on our walls, every element is chosen to elevate your experience and create an atmosphere of refined comfort.",
    card4_title: "Direct Accountability",
    card4_text:
      "Because we own and operate our homes, we are directly invested in the success of your stay and the long-term care of our properties. You deal with us, not a call center.",
  });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    setLoading(true);
    const home = await contentService.getPageContent("home");
    const about = await contentService.getPageContent("about");

    // Only overwrite defaults if data exists in DB
    if (home && Object.keys(home).length > 0)
      setHomeContent((prev) => ({ ...prev, ...home }));
    if (about && Object.keys(about).length > 0)
      setAboutContent((prev) => ({ ...prev, ...about }));

    setLoading(false);
  };

  const handleSave = async (pageId, data) => {
    setLoading(true);
    try {
      await contentService.updatePageContent(pageId, data);
      setMsg({ type: "success", text: "Content updated successfully!" });
    } catch (e) {
      setMsg({ type: "danger", text: "Failed to update content." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <Card.Header>Site Content Editor</Card.Header>
      <Card.Body>
        {msg && (
          <Alert variant={msg.type} onClose={() => setMsg(null)} dismissible>
            {msg.text}
          </Alert>
        )}

        <Tabs defaultActiveKey="home" className="mb-3">
          <Tab eventKey="home" title="Homepage">
            <Form>
              <h5 className="mb-3 text-primary">Hero Section</h5>
              <Form.Group className="mb-3">
                <Form.Label>Hero Headline (H1)</Form.Label>
                <Form.Control
                  type="text"
                  value={homeContent.h1}
                  onChange={(e) =>
                    setHomeContent({ ...homeContent, h1: e.target.value })
                  }
                />
              </Form.Group>
              <Form.Group className="mb-4">
                <Form.Label>Hero Subtext</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={homeContent.introText}
                  onChange={(e) =>
                    setHomeContent({
                      ...homeContent,
                      introText: e.target.value,
                    })
                  }
                />
              </Form.Group>

              <h5 className="mb-3 text-primary">
                Secondary Introduction (White Area)
              </h5>
              <Form.Group className="mb-3">
                <Form.Label>Paragraph 1</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={homeContent.secondary_intro_p1}
                  onChange={(e) =>
                    setHomeContent({
                      ...homeContent,
                      secondary_intro_p1: e.target.value,
                    })
                  }
                />
              </Form.Group>
              <Form.Group className="mb-4">
                <Form.Label>Paragraph 2</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={homeContent.secondary_intro_p2}
                  onChange={(e) =>
                    setHomeContent({
                      ...homeContent,
                      secondary_intro_p2: e.target.value,
                    })
                  }
                />
              </Form.Group>

              <h5 className="mb-3 text-primary">Headings</h5>
              <Form.Group className="mb-3">
                <Form.Label>Destinations Header</Form.Label>
                <Form.Control
                  type="text"
                  value={homeContent.h2_destinations}
                  onChange={(e) =>
                    setHomeContent({
                      ...homeContent,
                      h2_destinations: e.target.value,
                    })
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>"Why Choose" Header</Form.Label>
                <Form.Control
                  type="text"
                  value={homeContent.h2_why_choose}
                  onChange={(e) =>
                    setHomeContent({
                      ...homeContent,
                      h2_why_choose: e.target.value,
                    })
                  }
                />
              </Form.Group>
              <Form.Group className="mb-4">
                <Form.Label>"Why Choose" Subtext</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={homeContent.why_choose_subtext}
                  onChange={(e) =>
                    setHomeContent({
                      ...homeContent,
                      why_choose_subtext: e.target.value,
                    })
                  }
                />
              </Form.Group>

              <h5 className="mb-3 text-primary">Testimonials</h5>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Testimonial 1</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={homeContent.testimonial_1}
                      onChange={(e) =>
                        setHomeContent({
                          ...homeContent,
                          testimonial_1: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Testimonial 2</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={homeContent.testimonial_2}
                      onChange={(e) =>
                        setHomeContent({
                          ...homeContent,
                          testimonial_2: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Button
                disabled={loading}
                onClick={() => handleSave("home", homeContent)}
              >
                {loading ? <Spinner size="sm" /> : "Save Homepage"}
              </Button>
            </Form>
          </Tab>

          <Tab eventKey="about" title="About Page">
            <Form>
              {/* Header Section */}
              <h5 className="mb-3 text-primary">Header Section</h5>
              <Form.Group className="mb-3">
                <Form.Label>Page Title (H1)</Form.Label>
                <Form.Control
                  type="text"
                  value={aboutContent.h1}
                  onChange={(e) =>
                    setAboutContent({ ...aboutContent, h1: e.target.value })
                  }
                />
              </Form.Group>
              <Form.Group className="mb-4">
                <Form.Label>Intro Paragraph</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={aboutContent.introCopy}
                  onChange={(e) =>
                    setAboutContent({
                      ...aboutContent,
                      introCopy: e.target.value,
                    })
                  }
                />
              </Form.Group>

              {/* Family Section */}
              <h5 className="mb-3 text-primary">Family Section</h5>
              <Form.Group className="mb-3">
                <Form.Label>Section Title (H2)</Form.Label>
                <Form.Control
                  type="text"
                  value={aboutContent.h2_family}
                  onChange={(e) =>
                    setAboutContent({
                      ...aboutContent,
                      h2_family: e.target.value,
                    })
                  }
                />
              </Form.Group>
              <Form.Group className="mb-4">
                <Form.Label>Family Text</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={aboutContent.familyCopy}
                  onChange={(e) =>
                    setAboutContent({
                      ...aboutContent,
                      familyCopy: e.target.value,
                    })
                  }
                />
              </Form.Group>

              {/* ACF Standard Grid */}
              <h5 className="mb-3 text-primary">The ACF Standard Cards</h5>
              <Form.Group className="mb-3">
                <Form.Label>Grid Header (H2)</Form.Label>
                <Form.Control
                  type="text"
                  value={aboutContent.standard_h2}
                  onChange={(e) =>
                    setAboutContent({
                      ...aboutContent,
                      standard_h2: e.target.value,
                    })
                  }
                />
              </Form.Group>

              <Row>
                <Col md={6} className="mb-3">
                  <Card className="p-2 bg-light">
                    <Form.Label>
                      <strong>Card 1 (Eye Icon)</strong>
                    </Form.Label>
                    <Form.Control
                      className="mb-2"
                      placeholder="Title"
                      value={aboutContent.card1_title}
                      onChange={(e) =>
                        setAboutContent({
                          ...aboutContent,
                          card1_title: e.target.value,
                        })
                      }
                    />
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Text"
                      value={aboutContent.card1_text}
                      onChange={(e) =>
                        setAboutContent({
                          ...aboutContent,
                          card1_text: e.target.value,
                        })
                      }
                    />
                  </Card>
                </Col>
                <Col md={6} className="mb-3">
                  <Card className="p-2 bg-light">
                    <Form.Label>
                      <strong>Card 2 (Sparkle Icon)</strong>
                    </Form.Label>
                    <Form.Control
                      className="mb-2"
                      placeholder="Title"
                      value={aboutContent.card2_title}
                      onChange={(e) =>
                        setAboutContent({
                          ...aboutContent,
                          card2_title: e.target.value,
                        })
                      }
                    />
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Text"
                      value={aboutContent.card2_text}
                      onChange={(e) =>
                        setAboutContent({
                          ...aboutContent,
                          card2_text: e.target.value,
                        })
                      }
                    />
                  </Card>
                </Col>
                <Col md={6} className="mb-3">
                  <Card className="p-2 bg-light">
                    <Form.Label>
                      <strong>Card 3 (Palette Icon)</strong>
                    </Form.Label>
                    <Form.Control
                      className="mb-2"
                      placeholder="Title"
                      value={aboutContent.card3_title}
                      onChange={(e) =>
                        setAboutContent({
                          ...aboutContent,
                          card3_title: e.target.value,
                        })
                      }
                    />
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Text"
                      value={aboutContent.card3_text}
                      onChange={(e) =>
                        setAboutContent({
                          ...aboutContent,
                          card3_text: e.target.value,
                        })
                      }
                    />
                  </Card>
                </Col>
                <Col md={6} className="mb-3">
                  <Card className="p-2 bg-light">
                    <Form.Label>
                      <strong>Card 4 (Handshake Icon)</strong>
                    </Form.Label>
                    <Form.Control
                      className="mb-2"
                      placeholder="Title"
                      value={aboutContent.card4_title}
                      onChange={(e) =>
                        setAboutContent({
                          ...aboutContent,
                          card4_title: e.target.value,
                        })
                      }
                    />
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Text"
                      value={aboutContent.card4_text}
                      onChange={(e) =>
                        setAboutContent({
                          ...aboutContent,
                          card4_text: e.target.value,
                        })
                      }
                    />
                  </Card>
                </Col>
              </Row>

              <Button
                disabled={loading}
                onClick={() => handleSave("about", aboutContent)}
              >
                {loading ? <Spinner size="sm" /> : "Save About Page"}
              </Button>
            </Form>
          </Tab>
        </Tabs>
      </Card.Body>
    </Card>
  );
};

export default ContentManagement;
